import { ReactElement, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { PrimaryButton, SecondaryButton, Typography, useSnackbar, SnackbarStatus } from '@weareopenr/component-library';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import PriceSchemeSelectorBase from '../PriceSchemeSelector/PriceSchemeSelectorBase';
import { useValidate } from 'src/hooks/useValidate/useValidate';
import { getValidationRules } from 'src/utils/schedules/getValidationRules';
import { EVENT_TYPES } from 'src/api/getEvents/types';
import { useUserTracking } from 'src/hooks/useUserTracking/useUserTracking';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { getBrandId } from 'src/helpers/getBrandId/getBrandId';
import styles from './ValidationTrayContent.module.scss';

// Validates either a menu or a menu set - a menu set is validated as its member menus
export type ValidationTrayContentProps = {
  onClose: () => void;
  name: string;
} & ({ menuId: string; menuSetId?: never } | { menuSetId: string; menuId?: never });

export const getValidationTrayTitle = (isMenuSet: boolean) => (isMenuSet ? 'Validate menu set' : 'Validate menu');

export const ValidationTrayContent = ({
  onClose,
  menuId,
  menuSetId,
  name,
}: ValidationTrayContentProps): ReactElement => {
  // A run can be accepted after this tray has gone (cancelled, or the tray reopened); closing then would close
  // whichever tray is open by that time, so the close is skipped once this content has unmounted.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    [],
  );
  const [priceSchemeId, setPriceSchemeId] = useState<string | undefined>(undefined);
  const { showSnackbar } = useSnackbar();
  const { captureException } = useUserTracking();
  const queryClient = useQueryClient();
  const validate = useValidate();
  const entityLabel = menuSetId ? 'menu set' : 'menu';

  const handleValidate = async () => {
    if (!priceSchemeId) return;

    try {
      await validate.mutateAsync({
        criteria: {
          ...(menuSetId ? { menu_set_id: menuSetId } : { menu_id: menuId }),
          price_scheme_id: priceSchemeId,
          rules: getValidationRules(menuSetId ? EVENT_TYPES.PUBLISH_MENU_SET : EVENT_TYPES.PUBLISH_MENU),
        },
      });
      showSnackbar({ status: SnackbarStatus.PROGRESS, message: `Your ${entityLabel} validation is in progress` });
      if (isMountedRef.current) onClose();
      // Refetch so the menu set page sees VALIDATING and polls until the run finishes
      if (menuSetId) await queryClient.invalidateQueries({ queryKey: [QueryKeys.menuSet, getBrandId(), menuSetId] });
    } catch (err) {
      captureException(err, {
        tags: menuSetId ? { action: 'validate_menu_set', menuSetId } : { action: 'validate_menu', menuId },
      });
      showSnackbar({ status: SnackbarStatus.ERROR, message: `Failed to validate ${entityLabel}. Please try again.` });
    }
  };

  return (
    <div className={styles['content']}>
      <div className={styles['fields']}>
        <Typography size="medium" className={styles['subtitle']}>
          Choose a price scheme to validate <strong>{name}</strong> for missing prices, POS ID, Tax Rate, and more.
        </Typography>
        <div className={styles['section']}>
          <Typography weight="bold" size="medium">
            Pricing
          </Typography>
          <PriceSchemeSelectorBase value={priceSchemeId} onChange={setPriceSchemeId} />
        </div>
      </div>
      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <SecondaryButton
              buttonStyle="outline"
              type="button"
              size="full-width"
              onClick={onClose}
              disabled={validate.isLoading}
            >
              Cancel
            </SecondaryButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              onClick={handleValidate}
              disabled={!priceSchemeId || validate.isLoading}
              loading={validate.isLoading}
            >
              Run Validation
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export default ValidationTrayContent;
